import React, { Component, PropsWithChildren, ReactNode } from 'react'
import { lighten } from 'polished'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { CsCheckIcon } from '../icons'
import { rowActionListHoverCss } from './row-action-list'

const toBackgroundCss = (boundary, color) => {
  return `
    background-image: linear-gradient(
      to right,
      rgba(255,255,255,0),
      ${color} ${boundary}%,
      white ${100 - boundary}%,
      rgba(255,255,255,0)
    );
  `
}

const rowTransitionDuration = '0.15s'

const selectedBackground = css(toBackgroundCss(10, theme.color.white))
const hoverBackground = css`
  &:before {
    ${toBackgroundCss(14, theme.color.neutral10)};
  }
`

const checkMarkCellSize = 31

const cellCss = css`
  display: flex;
  flex: 1 0 auto;
  align-items: center;
  padding-bottom: 1px;
  label: cell;
`

const borderedCellCss = cx(
  'legacy-grid-bordered-cell',
  cellCss,
  css`
    border-bottom: 1px solid ${theme.color.neutral40};
    transition: border-color ${rowTransitionDuration};
    padding: 19px 0 20px 0;
    max-width: calc(100% - ${checkMarkCellSize}px);
  `
)

const gridCellDataID = 'grid-cell'

interface CellProps {
  readonly children: ReactNode
}

function Cell({ ...props }: CellProps) {
  return <div className={cx(cellCss)} data-testid={gridCellDataID} {...props} />
}

function BorderedCell({
  showShadow = false,
  gridCellTestID = gridCellDataID,
  ...props
}: CellProps & {
  readonly showShadow?: boolean
  readonly gridCellTestID?: string
}) {
  return (
    <div
      className={cx(
        borderedCellCss,
        showShadow &&
          css`
            box-shadow: rgb(238, 238, 238) 0 3px 1px -1px;
          `
      )}
      data-testid={gridCellTestID}
      {...props}
    />
  )
}

interface RowProps {
  readonly style: React.CSSProperties | undefined
  readonly children: ReactNode
  readonly isDrilldown: boolean
  readonly isMinimalGrid?: boolean
}

function LoadingRow({ style, children, isDrilldown, isMinimalGrid }: RowProps) {
  return (
    <div
      style={style}
      role="row"
      key="__Contrast_grid_loading_row"
      className={isDrilldown ? rowContainerDrilldownCss : rowContainerCss}
    >
      {!isMinimalGrid && <DisabledCheckMarkCell />}
      <BorderedCell>{children}</BorderedCell>
    </div>
  )
}

interface RowSelectIconProps {
  readonly selected: boolean
  readonly disabled?: boolean
  readonly hidden?: boolean
  readonly large?: boolean
}

const rowSelectDisabledCss = css`
  height: 15px;
  width: 15px;
  opacity: 0.8;
  transition: color ${rowTransitionDuration};
  color: ${theme.color.neutral40};
`

const rowSelectHiddenCss = cx(
  rowSelectDisabledCss,
  css`
    opacity: 0;
  `
)

const rowSelectIconCss = cx(
  rowSelectDisabledCss,
  css`
    &:hover {
      color: ${theme.color.neutral60};
    }
  `
)

const rowSelectIconSelectedCss = cx(
  rowSelectIconCss,
  css`
    color: ${theme.color.turquoise70};

    &:hover {
      color: ${theme.color.turquoise70};
    }
  `
)

const largeCss = css`
  transform: scale(1.5) translateX(2px);
`

const checkMarkCellDataID = 'grid-checkmark-cell'
function RowSelectIcon({
  selected,
  disabled,
  hidden = false,
  large,
  ...props
}: RowSelectIconProps) {
  return (
    <CsCheckIcon
      className={cx(
        hidden
          ? rowSelectHiddenCss
          : disabled
          ? rowSelectDisabledCss
          : selected
          ? rowSelectIconSelectedCss
          : rowSelectIconCss,
        !hidden && large ? largeCss : undefined
      )}
      data-testid={checkMarkCellDataID}
      {...props}
    />
  )
}

const checkMarkCellCss = cx(
  cellCss,
  css`
    flex: 0 0 ${checkMarkCellSize}px;
    padding: 0 8px;
  `
)

function SelectedRowSelectIcon(props) {
  return (
    <div className={selectableCheckMarkCellCss}>
      <RowSelectIcon selected={true} {...props} />
    </div>
  )
}

interface CheckMarkCellProps {
  readonly onClick: () => void
  readonly selected: boolean
  readonly 'data-testid'?: string
}

const selectableCheckMarkCellCss = cx(
  checkMarkCellCss,
  css`
    cursor: pointer;
  `
)

function CheckMarkCell({ selected, onClick, ...props }: CheckMarkCellProps) {
  return (
    <div onClick={onClick} className={selectableCheckMarkCellCss}>
      <RowSelectIcon selected={selected} {...props} />
    </div>
  )
}

function DisabledCheckMarkCell() {
  return (
    <div className={checkMarkCellCss}>
      <RowSelectIcon disabled={true} selected={false} />
    </div>
  )
}

function HiddenCheckMarkCell() {
  return (
    <div data-testid="grid-hidden-check-mark-cell" className={checkMarkCellCss}>
      <RowSelectIcon hidden={true} selected={false} />
    </div>
  )
}

interface RowContainerProps {
  readonly children: ReactNode
  readonly style: any
  readonly isDrilldown: boolean
  readonly isMinimalGrid?: boolean
}

const rowContainerSharedCss = css`
  display: flex;
  position: relative;
  line-height: 1;
`

const rowContainerCss = cx(
  rowContainerSharedCss,
  css`
    padding: 0 50px 0 27px;
  `
)

const rowContainerDrilldownCss = cx(
  rowContainerSharedCss,
  css`
    padding: 0 48px 0 40px;
  `
)

const rowContainerDrilldownMinimalCss = cx(
  rowContainerSharedCss,
  css`
    margin-left: 7px;
    padding: 0 28px 0 20px;
  `
)

export function RowContainer({
  isDrilldown,
  isMinimalGrid,
  ...props
}: RowContainerProps) {
  const additionalDrilldownCss = isMinimalGrid
    ? rowContainerDrilldownMinimalCss
    : rowContainerDrilldownCss
  const additionalCss = isMinimalGrid
    ? rowContainerDrilldownMinimalCss
    : rowContainerCss
  return (
    <div
      className={cx(
        'legacy-grid-row-container',
        isDrilldown ? additionalDrilldownCss : additionalCss
      )}
      role="row"
      {...props}
    />
  )
}

interface SelectableRowContainerProps extends RowContainerProps {
  readonly selected: boolean
  readonly active: boolean
}

const selectableRowContainerTransitionCss = css`
  background-size: 0;
  background-position: center;
  background-repeat: no-repeat;
  transition-property: background-size;
  transition-duration: ${rowTransitionDuration};
  transition-timing-function: cubic-bezier(0.95, 0.05, 0.795, 0.035);

  &:before {
    content: '';
    display: block;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    width: 100%;
    z-index: -1;
    transition: opacity ${rowTransitionDuration};
  }

  &:hover {
    &:before {
      opacity: 1;
    }

    [data-testid=${checkMarkCellDataID}] {
      color: ${theme.color.neutral60};
    }

    .${rowActionListHoverCss} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`

const selectableRowContainerTransitionActiveCss = css`
  &:before {
    opacity: 1;
  }
`

const selectableRowContainerCss = cx(
  rowContainerCss,
  hoverBackground,
  selectableRowContainerTransitionCss
)

const selectableRowContainerActiveCss = cx(
  rowContainerCss,
  hoverBackground,
  selectableRowContainerTransitionCss,
  selectableRowContainerTransitionActiveCss
)

const selectableRowContainerDrilldownCss = cx(
  rowContainerDrilldownCss,
  hoverBackground,
  selectableRowContainerTransitionCss
)

const selectableRowContainerActiveDrilldownCss = cx(
  rowContainerDrilldownCss,
  hoverBackground,
  selectableRowContainerTransitionCss,
  selectableRowContainerTransitionActiveCss
)

const selectedRowContainerTransitionCss = css`
  background-size: 100%;
  transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);

  [data-testid=${gridCellDataID}] {
    border-color: ${lighten(0.05, theme.color.neutral50)};
  }

  &:hover {
    [data-testid=${checkMarkCellDataID}] {
      color: ${theme.color.turquoise70};
    }
  }
`

const selectableRowContainerSelectedCss = cx(
  selectableRowContainerCss,
  selectedBackground,
  selectedRowContainerTransitionCss
)

const selectableRowContainerSelectedDrilldownCss = cx(
  selectableRowContainerDrilldownCss,
  selectedBackground,
  selectedRowContainerTransitionCss
)

const selectableRowContainerSelectedDrilldownMinimalCss = cx(
  selectableRowContainerDrilldownCss,
  selectedBackground,
  selectedRowContainerTransitionCss,
  css`
    margin-left: 0px;
    padding: 0 20px 0 5px;
  `
)

function getSelectableRowContainerClassName({
  selected,
  active,
  isDrilldown,
  isMinimalGrid
}: Partial<SelectableRowContainerProps>) {
  if (isMinimalGrid) {
    return selectableRowContainerSelectedDrilldownMinimalCss
  }

  if (selected) {
    if (isDrilldown) {
      return selectableRowContainerSelectedDrilldownCss
    }
    return selectableRowContainerSelectedCss
  }

  if (active) {
    return isDrilldown
      ? selectableRowContainerActiveDrilldownCss
      : selectableRowContainerActiveCss
  }

  if (isDrilldown) {
    return selectableRowContainerDrilldownCss
  }

  return selectableRowContainerCss
}

function SelectableRowContainer({
  selected,
  active,
  isDrilldown,
  isMinimalGrid,
  ...props
}: SelectableRowContainerProps) {
  return (
    <div
      className={cx(
        'legacy-grid-selectable-row-container',
        getSelectableRowContainerClassName({
          selected,
          active,
          isDrilldown,
          isMinimalGrid
        })
      )}
      role="row"
      {...props}
    />
  )
}

interface DataCellProps {
  readonly dataValue: unknown
  readonly otherDataValue?: unknown
  readonly rowHeightValue?: unknown
  readonly selected?: unknown
  readonly selectedCount?: unknown
  readonly showShadow?: boolean
  readonly isRowHovered?: boolean
  readonly gridCellTestID?: string
}

class DataCell extends Component<PropsWithChildren<DataCellProps>> {
  public render() {
    return (
      <BorderedCell
        showShadow={this.props.showShadow}
        gridCellTestID={this.props.gridCellTestID}
        children={this.props.children}
      />
    )
  }

  public shouldComponentUpdate({
    dataValue: prevDataValue,
    otherDataValue: prevOtherDataValue,
    rowHeightValue: prevRowHeightValue,
    selected: prevSelected,
    selectedCount: prevSelectedCount,
    showShadow: prevShowShadow,
    isRowHovered: prevIsRowHovered
  }: DataCellProps) {
    const {
      dataValue,
      otherDataValue,
      rowHeightValue,
      selected,
      selectedCount,
      showShadow,
      isRowHovered
    } = this.props
    return (
      dataValue !== prevDataValue ||
      otherDataValue !== prevOtherDataValue ||
      rowHeightValue !== prevRowHeightValue ||
      selected !== prevSelected ||
      selectedCount !== prevSelectedCount ||
      showShadow !== prevShowShadow ||
      isRowHovered !== prevIsRowHovered
    )
  }
}

interface SelectableRowProps extends RowProps {
  readonly onSelect: () => void
  readonly children: ReactNode
  readonly selected: boolean
  readonly selectedCount: number
  readonly active?: boolean
}

interface DataRowProps extends SelectableRowProps {
  readonly dataValue: unknown
  readonly otherDataValue?: unknown
  readonly rowHeightValue?: unknown
  readonly isSelectable: boolean
  readonly isDrilldown: boolean
  readonly showShadow: boolean
  readonly isRowHovered?: boolean
  readonly isMinimalGrid?: boolean
  readonly gridCellTestID?: string
}

function DataRow({
  style,
  children,
  selected,
  active = false,
  selectedCount,
  onSelect,
  dataValue,
  otherDataValue,
  rowHeightValue,
  isSelectable,
  isDrilldown,
  isMinimalGrid,
  showShadow,
  isRowHovered,
  gridCellTestID
}: DataRowProps) {
  return (
    <SelectableRowContainer
      data-e2e="row"
      data-testid={rowID}
      selected={selected}
      active={active}
      style={style}
      isDrilldown={isDrilldown}
      isMinimalGrid={isMinimalGrid}
    >
      {isSelectable ? (
        <CheckMarkCell
          onClick={onSelect}
          selected={selected}
          data-testid={checkMarkID}
          data-e2e={'grid-row-checkmark'}
        />
      ) : (
        <HiddenCheckMarkCell />
      )}
      <DataCell
        dataValue={dataValue}
        otherDataValue={otherDataValue}
        rowHeightValue={rowHeightValue}
        selected={selected}
        selectedCount={selectedCount}
        children={children}
        showShadow={showShadow}
        isRowHovered={isRowHovered}
        gridCellTestID={gridCellTestID}
      />
    </SelectableRowContainer>
  )
}

interface ColumnHeaderCellsProps {
  /**
   * This props is used to determine whether or not an
   * update to the column headers is warranted
   */
  readonly aggregations: unknown
  readonly aggregationSelections: unknown
  readonly filterValue?: unknown
  readonly isMinimalGrid?: boolean
}

class ColumnHeaderCells extends Component<
  PropsWithChildren<ColumnHeaderCellsProps>
> {
  public render() {
    return this.props.isMinimalGrid ? (
      <Cell children={this.props.children} />
    ) : (
      <BorderedCell children={this.props.children} />
    )
  }

  public shouldComponentUpdate(prevProps: ColumnHeaderCellsProps) {
    return (
      prevProps.aggregations !== this.props.aggregations ||
      prevProps.aggregationSelections !== this.props.aggregationSelections ||
      prevProps.filterValue !== this.props.filterValue
    )
  }
}

interface ColumnHeaderRowProps extends SelectableRowProps {
  /**
   * This props is used to determine whether or not an
   * update to the column headers is warranted
   */
  readonly aggregations?: {}
  readonly aggregationSelections?: {}
  readonly isDrilldown: boolean
  readonly isMinimalGrid?: boolean
  readonly filterValue?: unknown
}

function ColumnHeaderRow({
  style,
  children,
  selected,
  onSelect,
  aggregations,
  aggregationSelections,
  filterValue,
  isDrilldown,
  isMinimalGrid
}: ColumnHeaderRowProps) {
  return (
    <RowContainer
      data-testid={columnHeaderRowID}
      style={style}
      isDrilldown={isDrilldown}
      isMinimalGrid={isMinimalGrid}
    >
      {!isMinimalGrid && (
        <CheckMarkCell
          onClick={onSelect}
          selected={selected}
          data-testid={
            selected ? `${headerCheckMarkID}-selected` : headerCheckMarkID
          }
          data-e2e={'grid-header-checkmark'}
        />
      )}
      <ColumnHeaderCells
        children={children}
        aggregations={aggregations}
        aggregationSelections={aggregationSelections}
        filterValue={filterValue}
        isMinimalGrid={isMinimalGrid}
      />
    </RowContainer>
  )
}

export {
  DataRow,
  LoadingRow,
  CheckMarkCell,
  SelectedRowSelectIcon,
  ColumnHeaderRow
}

export const columnHeaderHeight = 40
export const loadingRowHeight = 60

// testing hooks
export const rowID = 'contrast-grid-row-0'
export const rowDataCellID = gridCellDataID
export const columnHeaderRowID = 'contrast-grid-column-header-row-1'
export const checkMarkID = 'contrast-grid-row-checkmark-2'
export const headerCheckMarkID = 'contrast-grid-header-checkmark-3'
